/**
 * Canonical brand registry — the SINGLE source of truth for which brand
 * (Ballee vs Flow) owns an outbound link or email, and what name / sender /
 * logo / base URL that brand uses.
 *
 * Why this lives in `@kit/shared`: the host-aware resolver historically lived
 * in `apps/web/lib/brand.ts`, which packages cannot import (apps depend on
 * packages, not the reverse). So every email/link built from a `@kit/*`
 * package fell back to the static `BRAND` const (hardcoded Flow) + the static
 * `NEXT_PUBLIC_SITE_URL` (Flow host), mis-branding Ballee-originated comms as
 * Flow. This registry is importable by BOTH packages and apps so there is one
 * resolution path. See
 * `docs/wip/active/WIP_unifying_brand_resolution_for_outbound_comms_2026_06_29.md`.
 *
 * This module is PURE (no `next/headers`, no I/O). The request-scoped resolver
 * that reads the host header lives in `apps/web/lib/brand-context.ts` and
 * delegates to {@link resolveBrandFromHost} here.
 */

export type BrandKey = 'ballee' | 'flow';

export interface BrandConfig {
  /** Stable brand identifier. */
  key: BrandKey;
  /** Display name used in email copy + UI chrome. */
  name: string;
  /** Short positioning line (SEO description / email preheader). */
  description: string;
  /**
   * Canonical absolute base URL for this brand's surface. Outbound links
   * (invites, notifications) MUST be built against this, never against
   * `NEXT_PUBLIC_SITE_URL`.
   */
  url: string;
  /** Email sender + contact addresses (display-name-qualified where sent). */
  emails: {
    noreply: string;
    notifications: string;
    support: string;
    /**
     * Super-admin operational inbox — cc'd on org self-claim / join-request
     * notifications so a platform admin sees every pending request. Optional:
     * only the org-canonical brand (Ballee) sets it; org flows always resolve
     * to that brand.
     */
    admin?: string;
  };
  /** Absolute image URLs + asset paths for email/UI logos. */
  logo: {
    /** Absolute PNG used inside HTML emails (must be a real, non-redirecting URL). */
    emailPng: string;
    /** App-relative SVG mark for in-app chrome. */
    svg: string;
    /** Absolute avatar used as the email sender/brand avatar. */
    avatar: string;
  };
}

/**
 * Default brand when a host cannot be resolved. Ballee is the primary brand;
 * Flow is the exception, resolved explicitly from a Flow host (BAL-402). The
 * highest-impact fallback (`emails.support`) is `support@ballee.co` for both
 * brands, so this flip is inert there and only corrects the truly ambiguous
 * cases (unresolved host, shop checkout without brand metadata) toward Ballee.
 */
export const DEFAULT_BRAND: BrandKey = 'ballee';

/**
 * The per-brand variant fields. Shared legal-entity data (company, bank,
 * registration) is NOT here — it is the same `Ballee Sàrl` entity for both
 * brands and stays on the legacy {@link BRAND} const.
 *
 * Flow values mirror the legacy static `BRAND` exactly so wiring Flow consumers
 * to the registry is a no-op; Ballee is the newly-correct variant.
 */
export const BRAND_REGISTRY: Record<BrandKey, BrandConfig> = {
  ballee: {
    key: 'ballee',
    name: 'Ballee',
    description: 'Dance Community & Discovery',
    url: 'https://ballee.co',
    emails: {
      // notifications.ballee.co is the Resend-verified sending domain (DKIM/SPF
      // configured per apps/web/supabase/config.prod.toml). Ballee uses it with
      // a Ballee display name.
      noreply: 'Ballee <noreply@notifications.ballee.co>',
      notifications: 'Ballee <noreply@notifications.ballee.co>',
      support: 'support@ballee.co',
      // Group inbox (Google Workspace), not a personal address — cc'd on org
      // join-request notifications. Managed via GAM (antoine@ is a manager).
      admin: 'admin@ballee.co',
    },
    logo: {
      emailPng: 'https://ballee.co/images/ballee-logo-email.png',
      svg: '/balleeLogo.svg',
      avatar: 'https://ballee.co/images/ballee-avatar.svg',
    },
  },
  flow: {
    key: 'flow',
    name: 'Flow',
    description: 'Artist Management Platform',
    url: 'https://flow.ballee.co',
    emails: {
      // Same verified sending domain as Ballee. The previous value,
      // on the unverified ballee.app domain, is refused by Resend with 403, so every Flow-context
      // email sent from brand.emails.noreply failed.
      noreply: 'Flow <noreply@notifications.ballee.co>',
      notifications: 'Flow <noreply@notifications.ballee.co>',
      support: 'support@ballee.co',
      admin: 'admin@ballee.co',
    },
    logo: {
      emailPng: 'https://flow.ballee.app/images/flow-logo-email.png',
      svg: '/flow-fmark.svg',
      avatar: 'https://flow.ballee.app/images/flow-avatar.svg',
    },
  },
};

/** Look up a brand config by key. */
export function getBrandConfig(key: BrandKey): BrandConfig {
  return BRAND_REGISTRY[key];
}

/**
 * Resolve a brand from a request hostname. PURE — the rules are the single
 * authority for host→brand and are kept in lock-step with
 * `apps/web/lib/routing/brand-hosts.ts`. `apps/web/lib/brand.ts`
 * `getBrandFromHostname` delegates here (Phase 2) so there is one rule set.
 */
export function resolveBrandFromHost(hostname: string): BrandKey {
  const host = hostname.toLowerCase().split(':')[0] ?? '';

  // Canonical production hosts.
  if (host === 'ballee.co' || host === 'www.ballee.co') return 'ballee';
  if (host === 'flow.ballee.co') return 'flow';

  // Staging hosts (auto-aliased to the latest `dev` deploy). Same split as prod.
  if (host === 'staging.ballee.co') return 'ballee';
  if (host === 'staging.flow.ballee.co') return 'flow';

  // Legacy Flow host (proxy.ts 301s flow.ballee.app → flow.ballee.co).
  if (host === 'flow.ballee.app') return 'flow';

  // Vercel preview deploys, anchored to our team scope so a third-party
  // `flow-evil.vercel.app` cannot resolve to a brand.
  if (
    /^(flow|ballee)-([a-z0-9-]+-)?antoineschallers?\.vercel\.app$/.test(host)
  ) {
    return host.startsWith('flow-') ? 'flow' : 'ballee';
  }

  // Local development: *.localhost prefix picks a brand without a restart.
  if (host === 'flow.localhost') return 'flow';
  if (host === 'ballee.localhost') return 'ballee';

  // Bare localhost → NEXT_PUBLIC_BRAND override, else default.
  if (host === 'localhost' || host === '127.0.0.1') {
    const envBrand = process.env.NEXT_PUBLIC_BRAND;
    if (envBrand === 'ballee' || envBrand === 'flow') return envBrand;
    return DEFAULT_BRAND;
  }

  return DEFAULT_BRAND;
}

/**
 * The entity kinds an outbound comm can originate from, for system/cron
 * contexts that have no request host (digests, reminders, webhooks). The
 * originating entity decides the brand:
 *   - competition / organization / audition / dancer profile → Ballee
 *   - agency / roster / workspace / team account → Flow
 */
export type BrandEntityKind =
  | 'competition'
  | 'organization'
  | 'audition'
  | 'dancer'
  | 'agency'
  | 'roster'
  | 'workspace'
  | 'account';

const BALLEE_ENTITY_KINDS: ReadonlySet<BrandEntityKind> = new Set([
  'competition',
  'organization',
  'audition',
  'dancer',
]);

/**
 * Resolve the brand for a system/cron context from the originating entity.
 * Used where there is no request host. Documented rule, never env-derived.
 */
export function resolveBrandForEntity(kind: BrandEntityKind): BrandConfig {
  return BALLEE_ENTITY_KINDS.has(kind)
    ? BRAND_REGISTRY.ballee
    : BRAND_REGISTRY.flow;
}
